import React, {ChangeEvent} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as RegistryConstants from 'constants/RegistryConstants';
import {isLabelingStrategy, RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import SetBySchemaInterfaceTooltip from 'schemas/schema-interface/SetBySchemaInterfaceTooltip';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {SettingsLabel, SettingsPopover} from 'ui-library/SettingsField';
import {Optional} from 'util/Types';

import * as LabelingStrategiesUtil from './LabelingStrategiesUtil';
import LabelingStrategyWarning from './LabelingStrategyWarning';

interface Props {
  labelingStrategies: Array<RegistryLabelingStrategy>;
  labelingStrategiesOverrideSource?: Optional<SchemaInterfaceSummary>;
  onChange: (newStrategies: Array<RegistryLabelingStrategy>) => void;
  nameTemplateParts: Array<NameTemplatePart>;
  isWritable: boolean;
  enforceLabelingStrategies?: boolean;
  setEnforceLabelingStrategies?: (enforceLabelingStrategies: boolean) => void;
}

function LabelingStrategyConfiguration(props: Props): JSX.Element {
  const handleLabelingStrategyChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const labelingStrategy = e.target.value;
    assert(
      isLabelingStrategy(labelingStrategy),
      `Selected value ${labelingStrategy} is not a valid labeling strategy`
    );
    if (props.labelingStrategies.includes(labelingStrategy)) {
      // eslint-disable-next-line no-restricted-syntax
      const newStrategies = _.without(props.labelingStrategies, labelingStrategy);
      props.onChange(newStrategies);
    } else {
      props.onChange([...props.labelingStrategies, labelingStrategy]);
    }
  };

  const handleEnforceLabelingStrategiesChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const checked = e.target.checked;
    assert(
      typeof props.setEnforceLabelingStrategies !== 'undefined',
      "Invalid option 'Enforce registration naming options'"
    );
    props.setEnforceLabelingStrategies(checked);
  };

  const popoverContent = (
    <div className="tagSchemaSettings-popover">
      <p>Each selected option will be available to the user when they register entities of this schema.</p>
      <p>
        We suggest limiting the number of options available to avoid different naming conventions across your
        organization.
      </p>
    </div>
  );

  const hasNameTemplate = props.nameTemplateParts.length > 0;

  const labelingStrategyOptions = [
    {
      value: RegistryLabelingStrategy.NEW_IDS,
    },
    {
      value: RegistryLabelingStrategy.IDS_FROM_NAMES,
    },
    {
      value: RegistryLabelingStrategy.DELETE_NAMES,
    },
    {
      value: RegistryLabelingStrategy.SET_FROM_NAME_PARTS,
      disabled: !hasNameTemplate,
    },
    {
      value: RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS,
      disabled: !hasNameTemplate,
    },
    {
      value: RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS,
      disabled: !hasNameTemplate,
    },
    {
      value: RegistryLabelingStrategy.KEEP_NAMES,
    },
  ];

  const helpTooltips = (
    <div>
      <SettingsPopover renderContent={() => popoverContent} placement={FloatingPlacement.TOP} />
      {props.labelingStrategiesOverrideSource ? (
        <span>
          {' '}
          <SetBySchemaInterfaceTooltip schemaInterfaceIdAndName={props.labelingStrategiesOverrideSource} />
        </span>
      ) : null}
    </div>
  );

  return (
    <div {...getTestProps({component: 'LabelingStrategyConfiguration', element: 'root'})}>
      <SettingsLabel
        isRequired={true}
        label="Available Registration Naming Options"
        helpIcon={helpTooltips}
      />
      <div>
        {typeof props.enforceLabelingStrategies !== 'undefined' ? (
          <div>
            <div
              key="enforce-naming-strategy"
              style={{borderBottomColor: Colors.GRAY, borderBottomWidth: 1, borderBottomStyle: 'solid'}}
            >
              <label className="u-normal-font-weight">
                <input
                  checked={props.enforceLabelingStrategies}
                  onChange={handleEnforceLabelingStrategiesChange}
                  disabled={!props.isWritable}
                  type="checkbox"
                />
                <span className="u-tiny-spacing-left">Enforce registration naming options</span>
              </label>
            </div>
          </div>
        ) : null}
        <div>
          {labelingStrategyOptions
            .filter(({value}) => LabelingStrategiesUtil.getEnabledLabelingStrategies().includes(value))
            .map(({value, disabled}) => (
              <div key={value} className="u-small-spacing-top">
                <label className="u-normal-font-weight u-no-spacing-bottom">
                  <input
                    checked={props.labelingStrategies.includes(value)}
                    onChange={handleLabelingStrategyChange}
                    disabled={
                      disabled ||
                      !props.isWritable ||
                      props.enforceLabelingStrategies === false ||
                      props.labelingStrategiesOverrideSource != null
                    }
                    type="checkbox"
                    value={value}
                  />
                  <span className="u-tiny-spacing-left">
                    {value === RegistryLabelingStrategy.REPLACE_ID_AND_NAME_FROM_PARTS && (
                      <LabelingStrategyWarning
                        labelingStrategies={props.labelingStrategies}
                        nameTemplateParts={props.nameTemplateParts}
                        className="u-tiny-spacing-right"
                      />
                    )}
                    {RegistryConstants.LABELING_STRATEGY_TO_TEXT[value]}
                  </span>
                </label>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default LabelingStrategyConfiguration;
